/**
 * Computes the offsets of a cursor position relative to an HTML text area that houses it.
 */
export const cursorOffsets = (input: HTMLTextAreaElement, selectionPoint: number) => {
	//
	// Our strategy for getting cursor offsets has 4 parts:
	//
	//   1. Create a div replica of the textarea with identical *computed* styles, e.g., height and
	//      width.
	//   2. Clone contents of textarea up to the cursor's position into the replica div.
	//   3. Create a "marker" span that contains the remainder of the textarea.
	//   4. Append marker span to replica div.
	//   5. Append replica div to the document body.
	//   6. Find the computed offsets of the marker span.
	//
	// Th offsets of the marker span correspond exactly to the offsets of the cursor relative to its
	// parent.
	//

	// Clone textarea as div.
	const div = cloneAsDiv(input);
	div.style.height = "auto";

	// Copy text in selection up to cursor into the dummy div.
	const inputValue = input.value;
	const textContent = inputValue.substring(0, selectionPoint);
	div.textContent = textContent;

	// Create marker element to measure offsets. Set `textContent` to everything after selection (or
	// dummy text if there's nothing).
	const span = document.createElement("span");
	span.textContent = inputValue.substring(selectionPoint) || ".";

	// Create span to calculate line height.
	const lineHeightSpan = document.createElement("span");
	lineHeightSpan.textContent = "A";

	// Add marker element to div, render div somewhere in document body.
	div.appendChild(span);
	document.body.appendChild(div);

	// Compute line height.
	div.appendChild(lineHeightSpan);
	const lineHeight = lineHeightSpan.offsetHeight;
	div.removeChild(lineHeightSpan);

	// Finally, get offsets for the marker span. This corresponds to the position of the cursor in
	// the text area.
	const {
		offsetTop: spanOffsetTop,
		offsetLeft: spanOffsetLeft,
		offsetHeight,
		offsetWidth,
		offsetParent,
	} = span;
	const offsetTop = spanOffsetTop - (span.parentElement?.offsetTop || 0);
	const offsetLeft = spanOffsetLeft - (span.parentElement?.offsetLeft || 0);

	// Remove the dummy element from the document body.
	document.body.removeChild(div);

	return {
		lineHeight,
		offsetTop,
		offsetLeft,
		offsetHeight,
		offsetWidth,
		offsetParent,
	};
};

/**
 * Clones a textarea as a div with identical *computed* styles.
 */
const cloneAsDiv = (toClone: HTMLTextAreaElement) => {
	// Create dummy element and clone the styles from the textarea.
	const div = document.createElement("div");

	const copyStyle = getComputedStyle(toClone);
	for (let i = 0; i < copyStyle.length; i++) {
		const prop = copyStyle[i];
		div.style.setProperty(prop, copyStyle.getPropertyValue(prop));
	}

	return div;
};
